// account-menu.ts — the console's seat on THE avatar menu (owner ruling
// 2026-09-08): the profile circle at the top-right of every Bridge page opens
// the ONE menu every surface opens — appearance on top (Dark/Light, the mode's
// theme, the style), then API tokens, then Log out. The module is
// pi-stack/src/client/avatar-menu.ts; this file binds it to the console's
// appearance seat (appearance.ts), its avatar renderer (admin-ui.ts
// avatarNode — the users grid draws the same circle) and its sign-out lane.
//
// RETIRED here, by that ruling: the gear (#hcThemeBtn) and the sun (#hcModeBtn)
// that theme-control.ts used to render into #hcAppearance, and the `items`
// seat this menu once carried. primitives-nav-check pins the retirements.
//
// PICTURE, THEN INITIALS. `users.picture_url` (migration 029) is populated
// from the OIDC `picture` claim, but three real cases produce none — a user
// invited and not yet logged in, an IdP that omits the claim, and everyone who
// last logged in before 029 shipped. So a missing picture is a NORMAL state,
// and initials are a first-class rendering rather than a placeholder.

import { avatarNode } from './admin-ui.js';
import {
  mountAvatarMenu, avatarInitials, avatarDisplayName, avatarSafePicture, type AvatarIdentity,
} from 'pi-stack/client/avatar-menu.js';
import {
  currentSeat, saveSeat, onAppearanceChange, pickableStyles, currentStyleId, type AppearanceSeat,
} from './appearance.js';
import { themesForMode } from './theme-control.js';
import { resolveTheme } from './generated/themes.js';
import { resolveStyle } from './generated/styles.js';
import { showToast } from 'pi-stack/client/toast.js';

export interface AccountUser {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'member';
  pictureUrl?: string | null;
}

/** Up to two initials from the best name available (name -> email local-part
 *  -> '?'). The shared module's rule, so one person is not "PH" on the home
 *  screen and "P" inside a release. */
export function initialsFor(user: Pick<AccountUser, 'name' | 'email'> | null): string {
  return avatarInitials(user);
}

/** The label beside the circle in the open menu. */
export function displayName(user: AccountUser | null): string {
  return avatarDisplayName(user);
}

/** Only absolute http(s) URLs are rendered — the second half of the server's
 *  own sanitizer (auth/oidc.ts), for rows written before it existed. */
export function safePictureUrl(raw: string | null | undefined): string | null {
  return avatarSafePicture(raw);
}

export interface AccountMenuOpts {
  /** Swappable for the tests; defaults to a real POST + reload. */
  onLogout?: () => void;
  /** Swappable for tests; defaults to the real seat writer. */
  save?: (seat: AppearanceSeat) => Promise<boolean> | boolean;
}

/**
 * Mount the account control into `host`. Returns a teardown, because the
 * shell re-mounts on every route change and the module's document-level
 * dismissal listeners would otherwise accumulate.
 */
export function mountAccountMenu(
  host: HTMLElement,
  user: AccountUser | null,
  opts: AccountMenuOpts = {},
): () => void {
  const save = opts.save ?? saveSeat;
  const doLogout = opts.onLogout ?? (() => {
    void (async () => {
      try {
        // Same-origin POST: the console's CSRF guard checks Origin, which the
        // browser sets for us. A GET logout would be triggerable from any page
        // on the internet.
        await fetch('/auth/logout', { method: 'POST' });
      } finally {
        // Unconditional: a failed logout must still take the user to the login
        // screen rather than leaving them on a page that looks signed in.
        location.href = '/auth/login';
      }
    })();
  });
  const identity: AvatarIdentity | null = user
    ? { email: user.email, name: user.name, pictureUrl: user.pictureUrl, role: user.role }
    : null;
  return mountAvatarMenu({
    host,
    identity,
    appearance: {
      seat: () => currentSeat(),
      themesForMode: (mode, styleId) => themesForMode(mode, styleId),
      styles: () => pickableStyles(),
      styleId: () => currentStyleId(),
      // R19: what a mode falls back to when this style registers no slot.
      fallbackThemeLabel: (mode) => resolveTheme('', mode, currentStyleId())?.label ?? null,
      styleLabel: () => resolveStyle(currentStyleId()).label,
      // #1125 item 3: the theme a style curates for a mode (resolveTheme of an
      // empty id IS defaultThemeFor), and the console's toast for the switch.
      defaultThemeId: (mode, styleId) => resolveTheme('', mode, styleId)?.id ?? null,
      notify: (message) => { showToast(message); },
      // saveSeat applies locally FIRST, so the page — AG Grid included, via
      // its paint-time var() params — reacts on the click.
      commit: (next) => save(next as AppearanceSeat),
      // No preview/endPreview: pickers apply on SELECT only (#3242).
      onChange: (fn) => onAppearanceChange(() => fn()),
    },
    tokensHref: '#/tokens',
    onLogout: user ? doLogout : null,
    place: 'below',
    // The historical ids — home-chrome.spec / admin-users.spec / the avatar
    // geometry check address the control by these names.
    ids: {
      btn: 'hcAccountBtn', menu: 'hcAccountMenu', themeSel: 'hcThemeSel', styleSel: 'hcStyleSel',
      tokens: 'hcTokensLink', logout: 'hcLogout',
    },
    // The console's own circle (.hc-avatar — the users grid draws the same
    // one, and rendering-seats pins its ONE size seat); `lg` for the menu copy.
    avatar: (id, size) => {
      const node = avatarNode(safePictureUrl(id?.pictureUrl), id?.name ?? null, id?.email ?? '');
      if (size === 'lg') node.classList.add('hc-avatar-lg');
      return node;
    },
  });
}
